import { Box, DialogActions, Divider, Stack, Typography } from '@mui/material';
import { getGeneralOptions } from 'API/generalOptions/generalOptions';
import { IPersonalDetails } from 'API/personalDetails';
import { NextPrevButton } from 'Components/NextPrevButton';
import { RequestsContext } from 'Context';
import { IUserOTPLoginDetails } from 'Context/OTPLoginContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  useAppSelector,
  useDictionary,
  useDispatchApp,
  useIsDesktop,
} from 'Hooks';
import { setGeneralOptions } from 'Store';
import { useCallback, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ISuccessDialog {
  title: string;
  reqNum: number;
  userData: IUserOTPLoginDetails | IPersonalDetails | null;
  resetRequest: () => void;
}

export const SuccessDialog = ({
  title,
  reqNum,
  userData,
  resetRequest,
}: ISuccessDialog) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const isDesktop = useIsDesktop();
  const { requestsData } = useContext(RequestsContext);
  const generalOptions = useAppSelector(
    (generalOptions) => generalOptions.generalOptions.generalOptions,
  );

  const navigate = useNavigate();

  const dispatch = useDispatchApp();

  const initializeGeneralOptions = useCallback(async () => {
    if (!generalOptions) {
      const generalOptionsResult = await getGeneralOptions();
      if (generalOptionsResult) {
        dispatch(setGeneralOptions(generalOptionsResult));
      }
    }
  }, [dispatch, generalOptions]);

  useEffect(() => {
    initializeGeneralOptions();
  }, [initializeGeneralOptions]);

  const fileNames = requestsData.fieldAttachmentsData
    .flatMap((obj) => (obj.attachments ? Object.keys(obj.attachments) : []))
    .join(' | ');

  const phone = `${
    generalOptions?.general.phonePrefixes.find(
      (prefix) =>
        prefix.id ===
        parseInt(
          userData?.mainPhonePrefix
            ? userData.mainPhonePrefix.toString()
            : (userData as IUserOTPLoginDetails).phonePrefix,
        ),
    )?.value || ''
  }${
    (userData?.mainPhone
      ? userData.mainPhone
      : (userData as IUserOTPLoginDetails).phoneNumber) || ''
  }`;

  const items = [
    {
      label: dictionary.successDialog.name,
      value: `${userData?.firstName} ${userData?.lastName}`,
    },
    {
      label: dictionary.successDialog.subject,
      value: title,
    },
    {
      label: dictionary.successDialog.userId,
      value: userData?.israeliId || (userData as IUserOTPLoginDetails)?.IsrId,
    },
    ...(phone !== ''
      ? [
          {
            label: dictionary.successDialog.phone,
            value: phone,
          },
        ]
      : []),
    {
      label: dictionary.successDialog.noteContent,
      value: requestsData.note,
    },
  ];

  return (
    <>
      <Box
        sx={{
          width: isDesktop ? '680px' : 'calc(100% - 32px)',
          borderRadius: '24px',
          background: '#fff',
          border: '1px solid #B7CCFF',
          boxShadow: '0px 4px 20px rgba(8, 30, 84, 0.12)',
          padding: isDesktop ? '32px 48px' : '32px',
          margin: '60px auto',
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            textAlign: 'center',
            pb: isDesktop ? 2 : 2,
          }}
        >
          <Typography
            sx={{
              p: isDesktop ? '0 0 16px' : '0 6px 16px',
            }}
            variant='h5'
            component='h2'
          >
            {dictionary.successDialog.title}
          </Typography>
          <Typography variant='body3'>
            {dictionary.successDialog.requestNumber} {reqNum}
          </Typography>

          <Stack
            sx={{
              gap: 2,
              p: '24px 0 32px',
              margin: '0 auto',
              maxWidth: '495px',
            }}
          >
            {items.map((item, index) => (
              <Stack
                key={`${item.label}-${index}`}
                sx={{
                  flexDirection: 'row',
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    minWidth:
                      item.label === dictionary.successDialog.noteContent
                        ? isDesktop
                          ? '85px'
                          : '68px'
                        : undefined,
                  }}
                  variant='body3'
                >
                  {item.label}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    textAlign: 'right',
                  }}
                >
                  {item.value.length > 93
                    ? `${item.value.substring(0, 93)}...`
                    : item.value}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {fileNames && requestsData.fieldAttachmentsData.length > 0 && (
            <>
              <Divider aria-hidden='true' sx={{ mb: 3, width: '100%' }} />

              {/* FILES NAME */}
              <Typography variant='body3' sx={{ mt: 2 }} component='h3'>
                {dictionary.successDialog.filesName}
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  mb: 5,
                  wordBreak: 'break-all',
                  wordWrap: 'break-word',
                }}
                variant='body2'
              >
                {fileNames}
              </Typography>
            </>
          )}

          <DialogActions
            sx={
              isDesktop
                ? undefined
                : {
                    width: '100%',
                    p: '0 20px',
                    justifyContent: 'center',
                    flexDirection: 'column-reverse',
                    gap: '20px',
                  }
            }
          >
            <NextPrevButton
              text={dictionary.successDialog.backToHomepage}
              callback={() => navigate('/')}
            />
            <NextPrevButton
              text={dictionary.successDialog.sendMoreRequest}
              isBlue
              isNext
              callback={resetRequest}
              style={{ button: { m: '0 8px 0 0 !important' } }}
            />
          </DialogActions>
        </Stack>
      </Box>
    </>
  );
};
